/**
 * labels — the two casing rules under every human string in the app, and the ONE acronym
 * table they share.
 *
 * The app had no such layer, so a machine string reached the screen on every surface that
 * lacked a private formatter: "Tpu Phone Case" in the template index, `multi_view` and
 * `ai_monthly_limit` as Entitlements field labels, "Save free" on a button, `Gpt` beside
 * `higgsfield` beside `Higgsfield` on one Providers screen, and a 64-character SHA-256 as
 * a device NAME in the Dashboard. Every one of those is fixed by asking this module.
 *
 * TWO casings, deliberately, because the two callers have incompatible requirements:
 *
 *   - `titleCase` is the FILESYSTEM rule. It is what `templateLabels.humanize` has always
 *     been, and it is the source of the export folder tree —
 *     `Mockups/<Product Type>/<Brand>/<Model>/<View>` (`batch/BatchPanel.tsx:54-58`, via
 *     categoryLabel → productTypesOf → ModelNode.product → selectionFromFunnel). Flipping
 *     it to sentence case would silently rename every customer's output folders and collide
 *     with WS-H1's run folders, so **its casing rule is frozen**; this wave gives it
 *     acronyms and nothing else.
 *   - `enumLabel` is the SCREEN rule, and it is new. Sentence case is correct there because
 *     it matches the canonical `PRODUCT_CATEGORIES` labels ("Phone case (TPU)") and AUD-7
 *     F-83's ruling against Title Case leaks ("AI Prompts" beside "AI providers").
 */

/** The one "no value" glyph the screen rules print. `titleCase` never returns it — see above. */
const EM_DASH = "—";

/**
 * Tokens that are acronyms, not words, and must survive both casing rules whole.
 *
 * Seeded with the acronyms this codebase actually prints. Exported so a later wave extends
 * the table in ONE place instead of adding a second private list. Matching is
 * case-insensitive, so `tpu`, `TPU` and `Tpu` all normalise to `TPU`.
 */
export const ACRONYMS: readonly string[] = [
  "TPU", "TPE", "PC", "PU",
  "GPT", "AI",
  "ID", "URL", "API", "R2", "CDN", "FTP", "SFTP",
  "DPI", "PNG", "JPG", "JPEG", "WEBP", "SVG",
  "SKU", "UUID",
  "CPU", "GPU", "IP", "UI", "UX",
  "QR", "NFC", "USB",
  "OLED", "LCD", "LED", "HD", "3D", "4K", "5G", "4G",
  "KPI", "SLA",
  "JSON", "CSV", "HTTP", "HTTPS", "TLS", "SSH",
];

/** Lower-cased lookup, built once. */
const ACRONYM_BY_LOWER = new Map(ACRONYMS.map((a) => [a.toLowerCase(), a]));

/**
 * Split a machine string into tokens: on `-`, `_` and whitespace, AND on a lowercase →
 * uppercase boundary so `aiMonthlyLimit` yields `ai | Monthly | Limit`. A digit run that
 * follows letters is left attached (`iqoo-15-5g` → `iqoo | 15 | 5g`, `oneplus-6` →
 * `oneplus | 6`), because splitting it would break model names.
 */
function tokenize(value: string): string[] {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[-_\s]+/)
    .filter(Boolean);
}

/** The acronym for a token, or `null` when it is an ordinary word. */
function acronymOf(token: string): string | null {
  return ACRONYM_BY_LOWER.get(token.toLowerCase()) ?? null;
}

/**
 * Title Case with acronyms — "tpu-clear" → "TPU Clear", "oneplus-6" → "Oneplus 6".
 *
 * Re-exported as `templates/templateLabels.humanize`, which is what nine WS-D2 consumers,
 * `mockups/funnel.ts` and `shared-ui/src/index.ts` call. Blank → `""` (NOT an em dash):
 * folder-name callers must be able to detect "nothing here" rather than create a directory
 * called `—`.
 */
export function titleCase(value: string | null | undefined): string {
  return tokenize(value ?? "")
    .map((w) => acronymOf(w) ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Sentence case with acronyms — the SCREEN rule for an internal key or an enum value.
 * "ai_monthly_limit" → "AI monthly limit", "gpt" → "GPT", "tenant_admin" → "Tenant admin".
 *
 * The first token is capitalised, the rest are lower-cased, and an acronym in either
 * position stays whole. Blank → an em dash, because a blank cell on screen is worse than
 * an explicit "no value".
 */
export function enumLabel(value: string | null | undefined): string {
  const tokens = tokenize(value ?? "");
  if (tokens.length === 0) return EM_DASH;
  return tokens
    .map((w, i) => {
      const acronym = acronymOf(w);
      if (acronym) return acronym;
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");
}

/** A device id long enough that reading it is hopeless — a SHA-256 is 64 hex characters. */
const LONG_HEX_ID_RE = /^[0-9a-f]{32,}$/i;

/**
 * Name a device instead of printing its hash — "Device c7db893a".
 *
 * A real device NAME passes through trimmed and is never rewritten; blank becomes an em
 * dash; and a SHORT hex id is left alone because it is a value an operator may actually
 * recognise and quote. Callers should keep the full id recoverable in a `title` — support
 * needs it, the person reading the screen does not.
 */
export function deviceLabel(id: string | null | undefined): string {
  const raw = (id ?? "").trim();
  if (!raw) return EM_DASH;
  if (LONG_HEX_ID_RE.test(raw)) return `Device ${raw.slice(0, 8)}`;
  return raw;
}
